import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import SessionTimeoutDialog from '../components/SessionTimeoutDialog';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

// URS-F428: Auto-logout por inactividad (21 CFR Part 11 / GDPR).
// 15 minutos totales de inactividad -> logout automatico.
// Los ultimos 60s se muestra un dialogo modal con cuenta atras y opcion
// "Continuar sesion" para prolongar la sesion.
const DEFAULT_INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 min
const DEFAULT_WARNING_BEFORE_MS = 60 * 1000;          // 60s de aviso final
const CHECK_INTERVAL_MS = 1000;                       // comprobacion cada 1s

// URS-F452: Sincronizacion de actividad entre pestanas.
// Antes, dos pestanas del mismo dominio tenian contadores independientes:
// una pestana en segundo plano expulsaba a las demas al pasar 15 min sin
// interaccion propia, aunque el usuario estuviera trabajando activamente
// en otra. Solucion: BroadcastChannel (con fallback a localStorage) para
// que la actividad en cualquier pestana reinicie el contador en todas.
const ACTIVITY_CHANNEL_NAME = 'ecrd-activity';
const ACTIVITY_STORAGE_KEY = 'ecrd_last_activity_ts';
const ACTIVITY_BROADCAST_THROTTLE_MS = 5000; // como maximo 1 broadcast/5s por pestana

// Permite override en tiempo de ejecucion para pruebas automatizadas.
// Ej: window.__ECRD_SESSION_TIMEOUT_MS__ = 10000; window.__ECRD_SESSION_WARNING_MS__ = 5000;
function getInactivityTimeoutMs() {
  if (typeof window !== 'undefined' && Number.isFinite(window.__ECRD_SESSION_TIMEOUT_MS__)) {
    return window.__ECRD_SESSION_TIMEOUT_MS__;
  }
  return DEFAULT_INACTIVITY_TIMEOUT_MS;
}
function getWarningBeforeMs() {
  if (typeof window !== 'undefined' && Number.isFinite(window.__ECRD_SESSION_WARNING_MS__)) {
    return window.__ECRD_SESSION_WARNING_MS__;
  }
  return DEFAULT_WARNING_BEFORE_MS;
}

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warningOpen, setWarningOpen] = useState(false);
  const [warningRemainingMs, setWarningRemainingMs] = useState(DEFAULT_WARNING_BEFORE_MS);
  // Track latest user inside the axios interceptor without restarting it on every render.
  const userRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const warningOpenRef = useRef(false);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { warningOpenRef.current = warningOpen; }, [warningOpen]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (err) {
      // URS-F191: Si /me falla con 401, el access_token (TTL 1h) puede haber
      // expirado pero el refresh_token (TTL 7 dias) seguir valido. Intentamos
      // refrescar la sesion explicitamente antes de dar al usuario por
      // deslogueado; de lo contrario, cada vez que el usuario vuelve a la app
      // tras > 1h aparece la pantalla de login aunque su sesion sea valida.
      if (err?.response?.status === 401) {
        try {
          await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
          const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
          setUser(data);
          setLoading(false);
          return;
        } catch {
          // refresh_token tambien invalido o ausente -> logout real
        }
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Auto-refresh: intercept 401s and retry with refresh token.
  // IMPORTANT: only trigger the refresh + logout logic when there IS a user
  // logged in. Otherwise a pre-login 401 (e.g., /api/permissions/me fired on
  // initial mount before the user logs in) would race with a successful login
  // and clear the freshly-set user, kicking the user back to /login.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      async err => {
        const original = err.config;
        const isAuthCall = original?.url?.includes('/auth/');
        const hasUser = userRef.current && userRef.current !== false;
        if (err.response?.status === 401 && !original._retry && !isAuthCall && hasUser) {
          original._retry = true;
          try {
            await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
            return axios(original);
          } catch {
            setUser(false);
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      // Reiniciar contador de inactividad al iniciar sesion
      lastActivityRef.current = Date.now();
      setWarningOpen(false);
      return { success: true, data };
    } catch (err) {
      return { success: false, error: formatApiError(err.response?.data?.detail) };
    }
  };

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setWarningOpen(false);
    setUser(false);
  }, []);

  // URS-F428: Rastrear actividad de usuario y disparar aviso/logout por inactividad.
  useEffect(() => {
    // Solo cuando hay un usuario autenticado.
    if (!user || user === false) return;

    lastActivityRef.current = Date.now();

    // URS-F452: Sincronizacion entre pestanas via BroadcastChannel + localStorage.
    // - Al detectar actividad local, broadcast (throttled a 5s) para que todas
    //   las pestanas actualicen su lastActivityRef.
    // - Al recibir un broadcast externo, actualizar el ref y cerrar el modal
    //   de aviso si estaba abierto (otra pestana esta trabajando).
    let bc = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel(ACTIVITY_CHANNEL_NAME);
        bc.onmessage = (evt) => {
          const ts = evt?.data?.ts;
          if (typeof ts === 'number' && ts > lastActivityRef.current) {
            lastActivityRef.current = ts;
            if (warningOpenRef.current) setWarningOpen(false);
          }
        };
      }
    } catch { /* BroadcastChannel no disponible; usamos solo localStorage */ }
    const onStorageActivity = (evt) => {
      if (evt.key !== ACTIVITY_STORAGE_KEY || !evt.newValue) return;
      const ts = parseInt(evt.newValue, 10);
      if (Number.isFinite(ts) && ts > lastActivityRef.current) {
        lastActivityRef.current = ts;
        if (warningOpenRef.current) setWarningOpen(false);
      }
    };
    window.addEventListener('storage', onStorageActivity);
    const lastBroadcastRef = { current: 0 };
    const broadcastActivity = (ts) => {
      if (ts - lastBroadcastRef.current < ACTIVITY_BROADCAST_THROTTLE_MS) return;
      lastBroadcastRef.current = ts;
      try { bc && bc.postMessage({ ts }); } catch { /* ignore */ }
      try { localStorage.setItem(ACTIVITY_STORAGE_KEY, String(ts)); } catch { /* ignore */ }
    };

    const handleActivity = () => {
      // URS-F428 fix: la ACTIVIDAD real del usuario (mover raton, teclear,
      // click, scroll, input) debe reiniciar SIEMPRE el contador de
      // inactividad, incluso si el modal de aviso ya esta abierto. En ese
      // caso, ademas, cerramos el modal automaticamente para no forzar al
      // usuario a hacer un click extra en "Continuar sesion" cuando ya
      // estaba trabajando.
      const now = Date.now();
      lastActivityRef.current = now;
      if (warningOpenRef.current) {
        setWarningOpen(false);
      }
      broadcastActivity(now);
    };

    // URS-F428 fix: TODOS los listeners en fase `capture` para que ningun
    // componente que llame a `event.stopPropagation()` en un handler de
    // burbuja pueda esconder la actividad del tracker global. Esto es lo
    // que hacia que a algunos usuarios se les cerrara la sesion aunque
    // estuvieran trabajando: modales/tablas internas cancelaban la
    // propagacion y `window` nunca veia el evento en burbuja.
    const activityEvents = [
      'mousemove', 'mousedown', 'keydown', 'keypress', 'touchstart',
      'click', 'wheel', 'pointermove', 'pointerdown',
      'scroll', 'input', 'change',
    ];
    activityEvents.forEach(e =>
      window.addEventListener(e, handleActivity, { passive: true, capture: true })
    );
    // URS-F452b: el simple hecho de que la pestana pase a `visible` NO
    // constituye actividad del usuario y por tanto NO debe resetear
    // `lastActivityRef`. Antes lo haciamos (`onVisibility -> handleActivity`)
    // y el efecto colateral era que si el usuario ocultaba la pestana (Alt-Tab,
    // pantalla bloqueada, ordenador durmiendo) durante mas de 15 min y luego
    // volvia, la sesion NO expiraba porque el propio evento visible reseteaba
    // el contador. Ahora solo escuchamos `visibilitychange` para lanzar un
    // check inmediato del `elapsed` (util cuando la pestana estuvo hidden y
    // el interval se salto los checks durante ese tiempo). Los broadcasts
    // desde otras pestanas si siguen actualizando `lastActivityRef` aunque
    // esta este oculta.
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      // Evaluar inmediatamente: si la pestana estuvo oculta lo suficiente
      // como para superar el timeout, el proximo tick del interval hara
      // logout. No forzamos aqui para dar tiempo a que otros efectos
      // (por ej. focus) actualicen el estado.
    };
    document.addEventListener('visibilitychange', onVisibility);
    // `focus` de window tampoco es actividad real del usuario (viene con
    // Alt-Tab). Antes tambien reseteaba el contador. Lo eliminamos por el
    // mismo motivo. Si el usuario interactua realmente, mousemove/click/keydown
    // ya haran su trabajo.
    window.addEventListener('message', handleActivity);

    const interval = setInterval(() => {
      // URS-F452: Si la pestana esta oculta (background), NO evaluamos el
      // timeout. Motivo: (1) el chequeo en pestanas en segundo plano
      // esta throttled por el navegador y puede llegar tarde; (2) si el
      // usuario esta trabajando en otra pestana ya recibimos su broadcast
      // y `lastActivityRef` esta al dia; (3) evita que una pestana oculta
      // dispare `logout()` (que borra la cookie httpOnly del servidor) y
      // arrastre a las demas pestanas activas del mismo dominio. Cuando la
      // pestana vuelve a ser visible, el listener `visibilitychange` ya
      // hace `handleActivity()` para reiniciar el contador.
      if (typeof document !== 'undefined' && document.hidden) return;
      const elapsed = Date.now() - lastActivityRef.current;
      const timeoutMs = getInactivityTimeoutMs();
      const warningMs = getWarningBeforeMs();
      const remaining = timeoutMs - elapsed;
      if (remaining <= 0) {
        // Tiempo agotado -> cerrar sesion
        logout();
      } else if (remaining <= warningMs) {
        setWarningRemainingMs(remaining);
        if (!warningOpenRef.current) setWarningOpen(true);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      activityEvents.forEach(e =>
        window.removeEventListener(e, handleActivity, { capture: true })
      );
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('message', handleActivity);
      window.removeEventListener('storage', onStorageActivity);
      try { bc && bc.close(); } catch { /* ignore */ }
      clearInterval(interval);
    };
  }, [user, logout]);

  const extendSession = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    setWarningOpen(false);
    // URS-F452: propagar a otras pestanas
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const bc = new BroadcastChannel(ACTIVITY_CHANNEL_NAME);
        bc.postMessage({ ts: now });
        bc.close();
      }
    } catch { /* ignore */ }
    try { localStorage.setItem(ACTIVITY_STORAGE_KEY, String(now)); } catch { /* ignore */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
      {user && user !== false && (
        <SessionTimeoutDialog
          open={warningOpen}
          remainingMs={warningRemainingMs}
          onExtend={extendSession}
          onLogoutNow={logout}
        />
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
